import React, { useState, useCallback } from 'react';
import { Box, Button, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  FilterContainer,
  FilterRow,
  StyledTextField,
  FilterCollapse,
  SecondaryFilterRow,
} from '../../users/UserFilters/UserFilters.styles';
import {
  FilterToolbarGradientButton,
  FilterToolbarOutlinedButton,
} from '../../../styles/theme/filterToolbarButtons';
import { DashboardDateRangePicker } from '../../dashboard/DashboardDateRangePicker';
import type { DateRangeValue } from '../../dashboard/DashboardDateRangePicker';
import type {
  ChartType,
  ComparisonMode,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ComparisonPeriod,
  StatisticsFilterState,
} from '../../../types/statistics.types';
import { CHART_TYPE_OPTIONS, CHARTS_WITH_PRODUCT_SELECTION } from '../../../types/statistics.types';
import ProductSearchSelect from './ProductSearchSelect';
import {
  getAvailableMonths,
  getAvailableYears,
  getPeriodKey,
  getPeriodLabel,
  parseMonthKey,
  parseYearKey,
} from './statisticsComparisonUtils';

function getRangeLabel(range: DateRangeValue): string {
  if (range.presetId === 'custom') {
    return `${format(range.startDate, 'd MMM', { locale: es })} - ${format(range.endDate, 'd MMM yyyy', { locale: es })}`;
  }
  const labels: Record<string, string> = {
    last7: 'Últimos 7 días',
    last30: 'Últimos 30 días',
    last90: 'Últimos 90 días',
    last365: 'Últimos 365 días',
    thisWeek: 'Esta semana',
    thisMonth: 'Este mes',
    thisYear: 'Este año',
    lastMonth: 'El mes pasado',
  };
  const label = labels[range.presetId] ?? range.presetId;
  return `${label} (${format(range.startDate, 'd MMM', { locale: es })} - ${format(range.endDate, 'd MMM', { locale: es })})`;
}

export interface StatisticsFiltersProps {
  filters: StatisticsFilterState;
  onFiltersChange: (filters: StatisticsFilterState) => void;
  onSearch: () => void;
  onReset: () => void;
  showMoreFilters: boolean;
  onToggleMoreFilters: () => void;
  /** Alineado con panel de control: mismo ColorModeContext que KPIBar y date picker */
  dashboardMode: 'light' | 'dark';
}

const StatisticsFilters: React.FC<StatisticsFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onReset,
  showMoreFilters,
  onToggleMoreFilters,
  dashboardMode,
}) => {
  const theme = useTheme();
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);

  const handleChartTypeChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const value = e.target.value as ChartType;
      const next = { ...filters, chartType: value };
      if (value !== 'product-trend' && next.selectedProductIds) {
        next.selectedProductIds = undefined;
      }
      onFiltersChange(next);
    },
    [filters, onFiltersChange],
  );

  const handleDateRangeChange = useCallback(
    (range: DateRangeValue) => {
      onFiltersChange({ ...filters, dateRange: range });
    },
    [filters, onFiltersChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onSearch();
    },
    [onSearch],
  );

  const isComparisonActive =
    filters.comparisonMode === 'months' || filters.comparisonMode === 'years';
  const availablePeriods =
    filters.comparisonMode === 'months'
      ? getAvailableMonths()
      : filters.comparisonMode === 'years'
        ? getAvailableYears().map((y) => ({ year: y }))
        : [];
  const baseKey = filters.basePeriod ? getPeriodKey(filters.basePeriod) : '';

  const showProductSelect = CHARTS_WITH_PRODUCT_SELECTION.includes(filters.chartType);
  const handleProductIdsChange = useCallback(
    (ids: number[]) => {
      onFiltersChange({ ...filters, selectedProductIds: ids });
    },
    [filters, onFiltersChange],
  );

  const handleComparisonModeChange = useCallback(
    (mode: ComparisonMode) => {
      const next: StatisticsFilterState = {
        ...filters,
        comparisonMode: mode,
        basePeriod: undefined,
        comparePeriods: undefined,
      };
      if (mode === 'months') {
        const months = getAvailableMonths();
        next.basePeriod = months[0];
        next.comparePeriods = months
          .slice(1, 2)
          .filter((m) => getPeriodKey(m) !== getPeriodKey(months[0]));
      } else if (mode === 'years') {
        const years = getAvailableYears();
        next.basePeriod = { year: years[0] };
        next.comparePeriods = years
          .slice(1, 2)
          .map((y) => ({ year: y }))
          .filter((p) => p.year !== years[0]);
      }
      onFiltersChange(next);
    },
    [filters, onFiltersChange],
  );

  return (
    <FilterContainer>
      <FilterRow sx={{ flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Tipo de gráfica"
            name="chartType"
            value={filters.chartType}
            onChange={(e) => handleChartTypeChange(e as SelectChangeEvent<string>)}
            onKeyDown={handleKeyDown}
            InputLabelProps={{ shrink: true }}
          >
            {CHART_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </StyledTextField>
        </Box>

        {showProductSelect && (
          <ProductSearchSelect
            value={filters.selectedProductIds ?? []}
            onChange={handleProductIdsChange}
            placeholder="Productos (1-4)"
            sx={{ minWidth: 300 }}
          />
        )}

        <StyledTextField
          select
          variant="outlined"
          size="medium"
          label="Comparar períodos"
          value={filters.comparisonMode ?? 'none'}
          onChange={(e) => handleComparisonModeChange(e.target.value as ComparisonMode)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="none">Sin comparación</MenuItem>
          <MenuItem value="months">Comparar meses</MenuItem>
          <MenuItem value="years">Comparar años</MenuItem>
        </StyledTextField>

        {!isComparisonActive ? (
          <Button
            variant="outlined"
            size="medium"
            onClick={(e) => setDatePickerAnchor(e.currentTarget)}
            startIcon={<CalendarIcon />}
            sx={{
              minWidth: 180,
              textTransform: 'none',
              fontSize: '13px',
              ...(dashboardMode === 'light'
                ? {
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.light,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    },
                  }
                : {
                    color: alpha(theme.palette.common.white, 0.85),
                    borderColor: alpha(theme.palette.common.white, 0.2),
                    '&:hover': {
                      borderColor: alpha(theme.palette.common.white, 0.4),
                      bgcolor: alpha(theme.palette.common.white, 0.05),
                    },
                  }),
            }}
          >
            {getRangeLabel(filters.dateRange)}
          </Button>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 180,
            }}
          >
            <CompareIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Rango definido por períodos
            </Typography>
          </Box>
        )}

        <FilterToolbarOutlinedButton
          variant="outlined"
          size="medium"
          onClick={onToggleMoreFilters}
          startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showMoreFilters ? 'Menos' : 'Más'}
        </FilterToolbarOutlinedButton>

        <FilterToolbarOutlinedButton
          variant="outlined"
          size="medium"
          onClick={onReset}
          startIcon={<RefreshIcon />}
        >
          Reiniciar
        </FilterToolbarOutlinedButton>

        <FilterToolbarGradientButton
          variant="contained"
          size="medium"
          onClick={onSearch}
          startIcon={<SearchIcon />}
        >
          Buscar
        </FilterToolbarGradientButton>
      </FilterRow>

      <DashboardDateRangePicker
        value={filters.dateRange}
        onChange={handleDateRangeChange}
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={() => setDatePickerAnchor(null)}
        mode={dashboardMode}
      />

      {isComparisonActive && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
            Base:
          </Typography>
          <StyledTextField
            select
            variant="outlined"
            size="small"
            value={baseKey}
            onChange={(e) => {
              const period =
                filters.comparisonMode === 'months'
                  ? parseMonthKey(e.target.value)
                  : parseYearKey(e.target.value);
              if (period) onFiltersChange({ ...filters, basePeriod: period });
            }}
            sx={{ minWidth: 120 }}
          >
            {availablePeriods.map((p) => (
              <MenuItem key={getPeriodKey(p)} value={getPeriodKey(p)}>
                {getPeriodLabel(p, filters.comparisonMode as 'months' | 'years')}
              </MenuItem>
            ))}
          </StyledTextField>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
            vs
          </Typography>
          {[0, 1, 2].map((i) => (
            <StyledTextField
              key={i}
              select
              variant="outlined"
              size="small"
              label={i === 0 ? 'Comparar' : `+${i + 1}`}
              value={filters.comparePeriods?.[i] ? getPeriodKey(filters.comparePeriods[i]) : ''}
              onChange={(e) => {
                const val = e.target.value;
                const period =
                  filters.comparisonMode === 'months' ? parseMonthKey(val) : parseYearKey(val);
                if (!period) return;
                const arr = [...(filters.comparePeriods ?? [])];
                arr[i] = period;
                onFiltersChange({ ...filters, comparePeriods: arr.filter(Boolean) });
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">—</MenuItem>
              {availablePeriods
                .filter((p) => getPeriodKey(p) !== baseKey)
                .map((p) => (
                  <MenuItem key={getPeriodKey(p)} value={getPeriodKey(p)}>
                    {getPeriodLabel(p, filters.comparisonMode as 'months' | 'years')}
                  </MenuItem>
                ))}
            </StyledTextField>
          ))}
        </Box>
      )}

      <FilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <SecondaryFilterRow sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mt: 2 }}>
          {/* Espacio para filtros adicionales futuros */}
        </SecondaryFilterRow>
      </FilterCollapse>
    </FilterContainer>
  );
};

export default StatisticsFilters;
